import React from 'react';
import { Routes, Route } from 'react-router-dom';
import { FluentProvider, webLightTheme, Theme } from '@fluentui/react-components';
import { useDarkMode } from '../contexts/DarkModeContext';
import HomeView from './views/HomeView';
import DomainView from './views/DomainView';
import CategoryView from './views/CategoryView';
import SubcategoryView from './views/SubcategoryView';
import IssueView from './views/IssueView';
import RootCauseDetailView from './views/RootCauseDetailView';
import DarkModeToggle from './DarkModeToggle';
import { TraceAppProps } from '../types';

/**
 * Main TRACE application component.
 * 
 * This component encapsulates all TRACE functionality and can be used either:
 * - Standalone: As the main application (wrapped with DarkModeProvider)
 * - Integrated: As a component within a larger dashboard
 * 
 * @example
 * // Standalone usage (with DarkModeProvider in App.tsx)
 * <TraceApp />
 * 
 * @example
 * // Dashboard integration
 * <TraceApp 
 *   basePath="/dashboard/trace"
 *   apiUrl="https://api.example.com"
 *   showDarkModeToggle={false}
 *   themeProvider={<FluentProvider theme={yourTheme}>...</FluentProvider>}
 * />
 */
const TraceApp: React.FC<TraceAppProps> = ({
  basePath = '/',
  apiUrl,
  showDarkModeToggle = true,
  themeProvider,
  onNavigate,
}) => {
  // When integrated, themeProvider should be provided and handle FluentProvider
  // When standalone, DarkModeProvider wraps this and provides theme via context
  const { theme } = useDarkMode();
  
  const appTheme: Theme = themeProvider ? webLightTheme : theme;

  // If themeProvider is provided, use it; otherwise create our own FluentProvider
  const appContent = (
    <>
      {showDarkModeToggle && !themeProvider && <DarkModeToggle />}
      <Routes>
        <Route path="/" element={<HomeView apiUrl={apiUrl} onNavigate={onNavigate} />} />
        <Route path="/:domain" element={<DomainView apiUrl={apiUrl} onNavigate={onNavigate} />} />
        <Route path="/:domain/:category" element={<CategoryView apiUrl={apiUrl} onNavigate={onNavigate} />} />
        <Route
          path="/:domain/:category/:subcategory"
          element={<SubcategoryView apiUrl={apiUrl} onNavigate={onNavigate} />}
        />
        <Route
          path="/:domain/:category/:subcategory/:issue"
          element={<IssueView apiUrl={apiUrl} onNavigate={onNavigate} />}
        />
        <Route
          path="/:domain/:category/:subcategory/:issue/:path"
          element={<RootCauseDetailView apiUrl={apiUrl} onNavigate={onNavigate} />}
        />
      </Routes>
    </>
  );

  if (themeProvider) {
    return <>{appContent}</>;
  }

  return (
    <FluentProvider theme={appTheme}>
      {appContent}
    </FluentProvider>
  );
};

export default TraceApp;
